import React, { useState } from "react";
import {
  Checkbox,
  Typography,
  TableRow,
  TableHead,
  TableCell,
  IconButton,
  Popper,
  Box,
  Select,
  MenuItem,
  TextField,
  Button,
  TableSortLabel,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { visuallyHidden } from "@mui/utils";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { ApprovalsTableHeadCell } from "./type";
import {
  DateFilterOperators,
  NumberFilterOperators,
  Order,
  StringFilterOperators,
} from "../../const/type";
import { ApprovalSortField } from "../../graphql/generated/graphql";

interface ApprovalsTableProps {
  headCells: ApprovalsTableHeadCell[];
  numSelected: number;
  rowCount: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order?: Order;
  orderBy?: string;
  onRequestSort?: (property: ApprovalSortField) => void;
  filters: Record<string, Record<string, string | Date | boolean>>;
  setFilters: (
    value: Record<string, Record<string, string | Date | boolean>>
  ) => void;
  role?: string;
}

export const ApprovalsTableHead = (props: ApprovalsTableProps) => {
  const {
    headCells,
    numSelected,
    rowCount,
    onSelectAllClick,
    orderBy,
    order = "desc",
    onRequestSort,
    filters,
    setFilters,
    role,
  } = props;

  const [tempFilterId, setTempFilterId] = useState("");
  const [tempFilterType, setTempFilterType] = useState("equalTo");
  const [tempFilterText, setTempFilterText] = useState<string | Date | boolean>(
    ""
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>, id: string) => {
    console.log(id);
    const existingFilter = filters[id];
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setTempFilterId(id);

    if (existingFilter) {
      const [operator] = Object.keys(existingFilter);
      setTempFilterType(operator);
      setTempFilterText(existingFilter[operator]);
    } else {
      if (["ministryCategory", "requestStatus" ].includes(id)) {
        setTempFilterType("iLike");
        setTempFilterText("");
      } else if (["organization", "firstName", "lastName"].includes(id)) {
          setTempFilterType("iLike");
          setTempFilterText("");
      } else if (
        [
          "createTime",
          "endDate",
          "acceptedDate",
          "requestTime",
          "shippedTime",
        ].includes(id)
      ) {
        setTempFilterType("lessThanEqual");
        setTempFilterText("");
      } else {
        setTempFilterType("equalTo");
        setTempFilterText("");
      }
    }
  };

  const handleApplyFilter = () => {
    if (!tempFilterId || tempFilterText === "") return;
    const newFilters = {
      ...filters,
      [tempFilterId]: {
        [tempFilterType]: tempFilterText,
      },
    };
    setFilters(newFilters);
    setAnchorEl(null);
  };
  const createSortHandler = (property: string) => {
    if (onRequestSort) onRequestSort(property as ApprovalSortField);
  };

  const handleClearFilter = (id: string) => {
    const updatedFilters = { ...filters };
    delete updatedFilters[id];
    setFilters(updatedFilters);
  };

  const isFilterActive = (id: string) => {
    return !!filters[id];
  };

  const open = Boolean(anchorEl);
  const popperId = open ? "filter-popper" : undefined;

  return (
    <TableHead>
      <TableRow>
        {role !== "admin" && (
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={numSelected === rowCount}
              onChange={onSelectAllClick}
            />
          </TableCell>
        )}
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.textAlign ?? "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={false}
            sx={{
              width: headCell?.minWidth,
              border: "1px solid rgba(224, 224, 224, 1)",
            }}
          >
            {headCell?.sortable ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-evenly"
              >
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order ?? "desc" : "asc"}
                  onClick={() => {
                    if (headCell.id !== "actions")
                      createSortHandler(headCell.id);
                  }}
                  sx={{ display: "flex", justifyContent: "space-evenly" }}
                >
                  <Typography
                    sx={{
                      ml: headCell?.ml,
                      fontSize: "0.875rem",
                      lineHeight: "1.5rem",
                      letterSpacing: "0.01071em",
                    }}
                  >
                    {headCell.label}
                  </Typography>
                  {orderBy === headCell.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </TableSortLabel>
                {!["actions", "id", "requestId"].includes(headCell.id) && (
                  <>
                    <IconButton
                      aria-describedby={popperId}
                      onClick={(event) => handleClick(event, headCell.id)}
                    >
                      {isFilterActive(headCell.id) ? (
                        <FilterAltIcon />
                      ) : (
                        <FilterAltOffIcon />
                      )}
                    </IconButton>
                    {isFilterActive(headCell.id) && (
                      <IconButton
                        onClick={() => handleClearFilter(headCell.id)}
                        size="small"
                      >
                        <FilterAltOffIcon fontSize="inherit" />
                      </IconButton>
                    )}
                  </>
                )}
                <Popper id={popperId} open={open} anchorEl={anchorEl}>
                  <Box bgcolor="#fff" p={1} display="flex">
                    <Select
                      label="Operator"
                      value={tempFilterType}
                      onChange={(event) =>
                        setTempFilterType(event.target.value)
                      }
                    >
                      {["id", "requestId"].includes(tempFilterId!) &&
                        NumberFilterOperators.map((item, index) => (
                          <MenuItem key={index} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      {["ministryCategory", "requestStatus", "organization", "firstName", "lastName"].includes(
                        tempFilterId!
                      ) &&
                        StringFilterOperators.map((item, index) => (
                          <MenuItem key={index} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      {[
                        "createTime",
                        "endDate",
                        "acceptedDate",
                        "requestTime",
                        "shippedTime",
                      ].includes(tempFilterId!) &&
                        DateFilterOperators.map((item, index) => (
                          <MenuItem key={index} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                    </Select>
                    {["id", "requestId"].includes(tempFilterId!) && (
                      <TextField
                        label="Value"
                        sx={{ mx: 1 }}
                        value={tempFilterText}
                        onChange={(event) =>
                          setTempFilterText(event.target.value)
                        }
                      />
                    )}
                    {["ministryCategory", "requestStatus", "organization", "firstName", "lastName"].includes(
                      tempFilterId!
                    ) && (
                      <TextField
                        label="Value"
                        sx={{ mx: 1 }}
                        value={tempFilterText}
                        onChange={(event) =>
                          setTempFilterText(event.target.value)
                        }
                      />
                    )}
                    {[
                      "createTime",
                      "endDate",
                      "acceptedDate",
                      "requestTime",
                      "shippedTime",
                    ].includes(tempFilterId!) && (
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          sx={{ width: 150, mx: 1 }}
                          onChange={(value: any) => setTempFilterText(value)}
                        />
                      </LocalizationProvider>
                    )}
                    <Button
                      variant="outlined"
                      disabled={tempFilterText === ""}
                      onClick={handleApplyFilter}
                    >
                      Apply
                    </Button>
                  </Box>
                </Popper>
              </Box>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-evenly"
              >
                <Typography
                  sx={{
                    width: headCell?.minWidth,
                    ml: headCell?.ml,
                    fontSize: "0.875rem",
                    lineHeight: "1.5rem",
                    letterSpacing: "0.01071em",
                  }}
                >
                  {headCell.label}
                </Typography>
                {!["actions"].includes(headCell.id) && (
                  <>
                    <IconButton
                      aria-describedby={popperId}
                      onClick={(event) => handleClick(event, headCell.id)}
                    >
                      {isFilterActive(headCell.id) ? (
                        <FilterAltIcon />
                      ) : (
                        <FilterAltOffIcon />
                      )}
                    </IconButton>
                    {isFilterActive(headCell.id) && (
                      <IconButton
                        onClick={() => handleClearFilter(headCell.id)}
                        size="small"
                      >
                        <FilterAltOffIcon fontSize="inherit" />
                      </IconButton>
                    )}
                  </>
                )}
                <Popper id={popperId} open={open} anchorEl={anchorEl}>
                  <Box bgcolor="#fff" p={1} display="flex">
                    <Select
                      label="Operator"
                      value={tempFilterType}
                      onChange={(event) =>
                        setTempFilterType(event.target.value)
                      }
                    >
                      {["id", "requestId"].includes(tempFilterId!) &&
                        NumberFilterOperators.map((item, index) => (
                          <MenuItem key={index} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      {["ministryCategory", "requestStatus", "organization", "firstName", "lastName"].includes(
                        tempFilterId!
                      ) &&
                        StringFilterOperators.map((item, index) => (
                          <MenuItem key={index} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      {[
                        "createTime",
                        "endDate",
                        "acceptedDate",
                        "requestTime",
                        "shippedTime",
                      ].includes(tempFilterId!) &&
                        DateFilterOperators.map((item, index) => (
                          <MenuItem key={index} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                    </Select>
                    {["id"].includes(tempFilterId!) && (
                      <TextField
                        label="Value"
                        sx={{ mx: 1 }}
                        value={tempFilterText}
                        onChange={(event) =>
                          setTempFilterText(event.target.value)
                        }
                      />
                    )}
                    {["ministryCategory", "requestStatus", "organization", "firstName", "lastName"].includes(
                      tempFilterId!
                    ) && (
                      <TextField
                        label="Value"
                        sx={{ mx: 1 }}
                        value={tempFilterText}
                        onChange={(event) =>
                          setTempFilterText(event.target.value)
                        }
                      />
                    )}
                    {[
                      "createTime",
                      "endDate",
                      "acceptedDate",
                      "requestTime",
                      "shippedTime",
                    ].includes(tempFilterId!) && (
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          sx={{ width: 150, mx: 1 }}
                          onChange={(value: any) => setTempFilterText(value)}
                        />
                      </LocalizationProvider>
                    )}
                    <Button
                      variant="outlined"
                      disabled={tempFilterText === ""}
                      onClick={handleApplyFilter}
                    >
                      Apply
                    </Button>
                  </Box>
                </Popper>
              </Box>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};
